import type { ReactNode } from 'react';
import type { PropertyOption, SpendingPurpose, TaxCategoryOption } from '@pet/shared';
import {
  ChipGroup,
  DESCRIPTION_REQUIRED_REASON,
  HelpButton,
  PurposeControls,
  SmallPurchaseControls,
  categoriesFor,
  shortPropertyLabel,
  type SpendLine,
} from './controls';
import styles from '../AddSpending.module.css';

// One split line's details: property, what it was for, its category, the small-purchase question
// and a description. The amount or percentage is typed on the assignment screen, not here.

export interface LineEditorProps {
  banner: ReactNode;
  line: SpendLine;
  properties: PropertyOption[];
  categories: TaxCategoryOption[];
  // Whether this line's property is pre-service on the payment date (AD-10), and whether its
  // description is therefore required. Both computed by the caller from the current draft.
  preService: boolean;
  descriptionRequired: boolean;
  onChange: (line: SpendLine) => void;
  onSelectProperty: (line: SpendLine, propertyId: string) => SpendLine;
  onSelectPurpose: (line: SpendLine, purpose: SpendingPurpose) => SpendLine;
  onSelectCategory: (line: SpendLine, key: string) => SpendLine;
  onRemove: () => void;
  onDone: () => void;
  // Open the Guide section for "What was this for?" / the small-purchase question ("?" help).
  onHelpPurpose?: () => void;
  onHelpSmallPurchase?: () => void;
}

export default function LineEditor({
  banner,
  line,
  properties,
  categories,
  preService,
  descriptionRequired,
  onChange,
  onSelectProperty,
  onSelectPurpose,
  onSelectCategory,
  onRemove,
  onDone,
  onHelpPurpose,
  onHelpSmallPurchase,
}: LineEditorProps) {
  const needsDescription = descriptionRequired && line.description.trim() === '';
  const showCategories = line.purpose !== '' && line.purpose !== 'improvement';
  return (
    <main className={styles.container}>
      {banner}
      <h2 className={styles.question}>Line details</h2>
      <label className={styles.fieldLabel}>Property</label>
      <ChipGroup
        ariaLabel="Property"
        options={properties.map((p) => ({ value: p.id, label: shortPropertyLabel(p.address) }))}
        selected={line.propertyId}
        onSelect={(propertyId) => onChange(onSelectProperty(line, propertyId))}
      />
      {onHelpPurpose ? (
        <div className={styles.questionRow}>
          <label className={styles.fieldLabel}>What was this for?</label>
          <HelpButton question="What was this for?" onClick={onHelpPurpose} />
        </div>
      ) : (
        <label className={styles.fieldLabel}>What was this for?</label>
      )}
      <PurposeControls
        line={line}
        preService={preService}
        onSelect={(purpose) => onChange(onSelectPurpose(line, purpose))}
      />
      {showCategories && (
        <>
          <label className={styles.fieldLabel}>Category</label>
          <ChipGroup
            ariaLabel="Tax category"
            options={categoriesFor(categories, line.purpose).map((c) => ({ value: c.key, label: c.label }))}
            selected={line.taxCategoryKey}
            onSelect={(key) => onChange(onSelectCategory(line, key))}
          />
        </>
      )}
      {line.taxCategoryKey && (
        <SmallPurchaseControls line={line} categories={categories} onChange={onChange} onHelp={onHelpSmallPurchase} />
      )}
      <label className={styles.fieldLabel} htmlFor="line-desc">
        {descriptionRequired ? 'Description (required)' : 'Description (optional)'}
      </label>
      <input
        id="line-desc"
        className={styles.textInput}
        type="text"
        value={line.description}
        onChange={(e) => onChange({ ...line, description: e.target.value, descriptionEdited: true })}
        aria-label="Description"
      />
      {needsDescription && <p className={styles.overrideNote}>{DESCRIPTION_REQUIRED_REASON}</p>}
      <button className={styles.removeButton} onClick={onRemove}>
        Remove this line
      </button>
      <div className={styles.navRow}>
        <button
          className={styles.primaryButton}
          disabled={!line.propertyId || !line.taxCategoryKey || needsDescription}
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </main>
  );
}
